<template>
  <div class="relative z-50">
    <div
      class="w-7 h-7 bg-black hover:bg-truegray-700 rounded-full p-2 text-white cursor-pointer transition-all duration-100 relative"
      @click="handleClick(postId)"
    >
      <div
        class="absolute inset-0 w-full h-full flex justify-center items-center z-20"
      >
        <svg
          v-if="!thinking"
          xmlns="http://www.w3.org/2000/svg"
          class="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
          />
        </svg>
        <lottie v-if="thinking" :options="loadingAnimationOptions" />
      </div>
    </div>
    <ul
      :class="[
        active
          ? 'opacity-100 pointer-events-all translate-y-0'
          : '-translate-y-8 opacity-0 pointer-events-none',
      ]"
      class="absolute top-full mt-4 rounded shadow-xl right-0 bg-white text-truegray-500 transform transition-all duration-300 ease-in-out w-40 z-50"
    >
      <li
        class="hover:text-black hover:bg-truegray-300 p-4 cursor-pointer border-b border-truegray-300"
        @click="editPost(postId)"
      >
        Edit post
      </li>
      <li
        v-if="!isContributor"
        class="hover:text-black hover:bg-truegray-300 p-4 cursor-pointer border-b border-truegray-300"
        @click="bumpPost(postId)"
      >
        Bump post
      </li>
      <!-- <li class="hover:text-black hover:bg-truegray-300 p-4 cursor-pointer border-b border-truegray-300" @click="duplicatePost(postId)">Duplicate post to drafts</li> -->
      <li
        v-if="status === 0"
        class="hover:text-black hover:bg-truegray-300 p-4 cursor-pointer border-b border-truegray-300"
        @click="publishPost(postId)"
      >
        Publish
      </li>
      <li
        class="text-red-500 hover:bg-truegray-300 p-4 cursor-pointer"
        @click="showPostDeleteModal(post_parent)"
      >
        Delete post
      </li>
    </ul>
  </div>
</template>

<script>
import { EventBus } from '../../js/plugins/event-bus.js'
import { mapActions, mapGetters } from 'vuex'
import * as loadingAnimation from '../../shared/components/loading.json'
import { shared } from '../../js/mixins/shared.js'

export default {
  mixins: [shared],

  props: {
    postId: { default: null },
    status: { default: null },
    parent: { default: false },
    post_parent: { default: false },
  },

  data() {
    return {
      loadingAnimationOptions: { animationData: loadingAnimation.default },
      loadingAnimate: false,
      active: false,
      thinking: false,
    }
  },

  computed: {
    ...mapGetters({
      client: 'shared/getClient',
      userAccess: 'shared/getUserAccess',
      isContributor: 'shared/isContributor',
    }),
  },

  mounted() {
    EventBus.$on('confirmDeletePost', (post_parent_id) => {
      if (post_parent_id === this.post_parent) {
        this.deletePost(this.postId)
      }
    })

    EventBus.$on('closeOtherPostsManagerMenus', () => {
      this.$set(this, 'active', false)
    })
  },

  methods: {
    ...mapActions({
      dispatchSetFilterPostsBy: 'shared/dispatchSetFilterPostsBy',
      dispatchUpdateContributorPost: 'contributors/dispatchUpdatePost',
    }),

    bumpPost(id) {
      axios
        .post('api/auth/set-post-published-date', { post_id: id })
        .then((response) => {
          this.$set(this, 'thinking', false)
          if (response.data.success) {
            this.dispatchSetFilterPostsBy({ reset: true })
            EventBus.$emit('getPostsWithFilterOptions', this.parent)
          }
          if (response.data.alert) {
            EventBus.$emit('triggerAlertResponse', response.data.alert)
          }
          EventBus.$emit('closeOtherPostsManagerMenus')
        })
    },

    duplicatePost(id) {
      axios
        .post('api/auth/duplicate-post', { post_id: id })
        .then((response) => {
          this.$set(this, 'thinking', false)
          if (response.data.success) {
            this.dispatchSetFilterPostsBy({ reset: true })
            EventBus.$emit('getPostsWithFilterOptions', this.parent)
          }
          if (response.data.alert) {
            EventBus.$emit('triggerAlertResponse', response.data.alert)
          }
          EventBus.$emit('closeOtherPostsManagerMenus')
        })
    },

    editPost(id) {
      if (this.isContributor) {
        this.$router.push({
          name: 'ContributorsPost',
          params: { post_id: id, edit: true },
        })
      } else if (this.$router.history.current.path !== '/post/' + id) {
        this.$router.push({ name: 'Post', params: { post_id: id, edit: true } })
      }
    },

    showPostDeleteModal(post_parent) {
      if (this.isContributor) {
        EventBus.$emit('showModal', {
          action: 'post-delete',
          data: {
            postId: this.postId,
          },
        })
      } else {
        EventBus.$emit('showPostDeleteModal', post_parent)
      }
    },

    deletePost(id = false) {
      if (!id || !this.userAccess) {
        return
      }

      this.$set(this, 'thinking', true)

      axios.get('/api/auth/delete-post/' + id).then((response) => {
        this.$set(this, 'thinking', false)
        this.$set(this, 'active', false)

        EventBus.$emit('showPostDeleteModal', false)

        EventBus.$emit('closeOtherPostsManagerMenus')

        // Bad name. Previously was calling new posts, but unable to check for 'old events' without access to filter component.
        // Defer to filter component to handle this.
        EventBus.$emit('getUpdatedPostsFromFilterComponent')
        EventBus.$emit('reloadWebsiteSettings')

        if (response.data.alert) {
          EventBus.$emit('triggerAlertResponse', response.data.alert)
        }
      })
    },

    async publishPost(id) {
      if (!id) {
        return
      }

      const handleSuccess = () => {
        this.$set(this, 'active', false)
        this.$set(this, 'thinking', false)
        EventBus.$emit('closeOtherPostsManagerMenus')
        EventBus.$emit('getPostsWithFilterOptions', this.parent)
      }

      if (this.isContributor) {
        await this.dispatchUpdateContributorPost({
          quick_publish: true,
          status: true,
          post_id: id,
        })
        handleSuccess()
      } else {
        let endpoint = 'update-post'
        let client = this.client
        if (client === 'client2tenant') {
          endpoint = 'update-post-with-relic'
        }

        this.$set(this, 'thinking', true)

        let submitData = new FormData()
        submitData.append('quick_publish', 'true')
        submitData.append('status', 1)
        submitData.append('post_id', id)
        axios.post('/api/auth/' + endpoint, submitData).then((response) => {
          handleSuccess()

          if (response.data.alert) {
            EventBus.$emit('triggerAlertResponse', response.data.alert)
          }
        })
      }
    },

    handleClick(id) {
      // @todo test `id = this.postId`
      if (id === this.postId && this.active === true) {
        this.$set(this, 'active', false)
      } else {
        EventBus.$emit('closeOtherPostsManagerMenus')
        this.$set(this, 'active', this.active ? false : true)
      }
    },
  },
}
</script>
